import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  // Button,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  // Select,
  // MenuItem,
  // FormControl,
  // InputLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  // Tooltip
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  DirectionsCar as CarIcon,
  // Schedule as ScheduleIcon,
  // Route as RouteIcon,
  Person as PersonIcon,
  // Store as StoreIcon,
  Assessment as AssessmentIcon,
  CompareArrows as CompareIcon,
  Delete as DeleteIcon,
  // Visibility as ViewIcon
} from '@mui/icons-material';
import axios from 'axios';

interface OptimizationRun {
  id: number;
  clustering_algorithm: string;
  cluster_count: number;
  transport_type: string;
  status: string;
  created_at: string;
  total_distance_km?: number;
  total_time_hours?: number;
  total_visits_scheduled?: number;
  total_visits_required?: number;
  optimization_score?: number;
}

interface DailySchedule {
  id: number;
  worker_id: number;
  day_index: number;
  date?: string;
  store_count: number;
  total_visits: number;
  total_distance_km: number;
  total_time_hours: number;
  utilization_percentage: number;
  visits: ScheduledVisit[];
}

interface ScheduledVisit {
  id: number;
  visit_order: number;
  store_name: string;
  store_address: string;
  arrival_time: string;
  departure_time: string;
  visit_duration_minutes: number;
  travel_time_minutes: number;
  travel_distance_km: number;
}

interface Worker {
  id: number;
  name: string;
}

interface Props {
  planId: number;
}

export const OptimizationResultsView: React.FC<Props> = ({ planId }) => {
  const [runs, setRuns] = useState<OptimizationRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<OptimizationRun | null>(null);
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const [dailySchedules, setDailySchedules] = useState<DailySchedule[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const API_BASE_URL = 'http://127.0.0.1:8000/api/v1';

  useEffect(() => {
    loadOptimizationRuns();
    loadWorkers();
  }, [planId]);

  useEffect(() => {
    if (selectedRun && selectedRun.status === 'completed') {
      loadDailySchedules();
    }
  }, [selectedRun, selectedDay]);

  const loadOptimizationRuns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/optimization-results/plans/${planId}/runs`);
      setRuns(response.data);
      if (response.data.length > 0 && !selectedRun) {
        setSelectedRun(response.data[0]);
      }
    } catch (err) {
      setError('Failed to load optimization runs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/plans/${planId}/workers`);
      setWorkers(response.data);
    } catch (err) {
      console.error('Failed to load workers', err);
    }
  };

  const loadDailySchedules = async () => {
    if (!selectedRun) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/optimization-results/runs/${selectedRun.id}/daily-schedules?day_index=${selectedDay}`
      );
      setDailySchedules(response.data);
    } catch (err) {
      setError('Failed to load daily schedules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteOptimizationRun = async (runId: number) => {
    if (!window.confirm('Are you sure you want to delete this optimization run?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE_URL}/optimization-results/runs/${runId}`);
      loadOptimizationRuns();
      if (selectedRun?.id === runId) {
        setSelectedRun(null);
      }
    } catch (err) {
      setError('Failed to delete optimization run');
      console.error(err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTime = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const renderRunsList = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Algorithm</TableCell>
            <TableCell>Clusters</TableCell>
            <TableCell>Transport</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Coverage</TableCell>
            <TableCell>Distance</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Score</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run) => (
            <TableRow
              key={run.id}
              selected={selectedRun?.id === run.id}
              onClick={() => setSelectedRun(run)}
              style={{ cursor: 'pointer' }}
            >
              <TableCell>{run.clustering_algorithm}</TableCell>
              <TableCell>{run.cluster_count}</TableCell>
              <TableCell>
                <CarIcon fontSize="small" /> {run.transport_type}
              </TableCell>
              <TableCell>
                <Chip
                  label={run.status}
                  color={getStatusColor(run.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {run.total_visits_scheduled && run.total_visits_required
                  ? `${Math.round((run.total_visits_scheduled / run.total_visits_required) * 100)}%`
                  : '-'}
              </TableCell>
              <TableCell>
                {run.total_distance_km ? `${run.total_distance_km.toFixed(1)} km` : '-'}
              </TableCell>
              <TableCell>
                {run.total_time_hours ? formatTime(run.total_time_hours) : '-'}
              </TableCell>
              <TableCell>
                {run.optimization_score ? `${run.optimization_score.toFixed(1)}` : '-'}
              </TableCell>
              <TableCell>
                {new Date(run.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteOptimizationRun(run.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderDailySchedule = () => {
    if (!selectedRun || selectedRun.status !== 'completed') {
      return (
        <Alert severity="info">
          Select a completed optimization run to view daily schedules
        </Alert>
      );
    }

    const days = Array.from({ length: 7 }, (_, i) => i);
    
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Tabs value={selectedDay} onChange={(_, value) => setSelectedDay(value)}>
            {days.map((day) => (
              <Tab
                key={day}
                label={`Day ${day + 1}`}
                icon={<CalendarIcon />}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {loading ? (
          <CircularProgress />
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {dailySchedules.map((schedule) => {
              const worker = workers.find(w => w.id === schedule.worker_id);
              
              return (
                <Box key={schedule.id} sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PersonIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          {worker?.name || `Worker ${schedule.worker_id}`}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Stores: {schedule.store_count}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Distance: {schedule.total_distance_km.toFixed(1)} km
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Time: {formatTime(schedule.total_time_hours)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Utilization: {schedule.utilization_percentage.toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />
                      
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Route Schedule:
                      </Typography>
                      
                      <List dense>
                        {schedule.visits
                          .sort((a, b) => a.visit_order - b.visit_order)
                          .map((visit, _index) => (
                            <ListItem key={visit.id} disablePadding>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                      {visit.visit_order}.
                                    </Typography>
                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                      {visit.store_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {visit.arrival_time} - {visit.departure_time}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {visit.store_address}
                                    {_index > 0 && ` • ${visit.travel_distance_km.toFixed(1)}km, ${visit.travel_time_minutes}min`}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const renderComparison = () => {
    const completedRuns = runs.filter(r => r.status === 'completed');
    
    if (completedRuns.length < 2) {
      return (
        <Alert severity="info">
          Need at least 2 completed optimization runs to compare
        </Alert>
      );
    }
    
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              {completedRuns.map(run => (
                <TableCell key={run.id} align="center">
                  {run.clustering_algorithm}
                  <br />
                  <Typography variant="caption">
                    {new Date(run.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Coverage %</TableCell>
              {completedRuns.map(run => (
                <TableCell key={run.id} align="center">
                  {run.total_visits_scheduled && run.total_visits_required
                    ? `${Math.round((run.total_visits_scheduled / run.total_visits_required) * 100)}%`
                    : '-'}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Total Distance</TableCell>
              {completedRuns.map(run => (
                <TableCell key={run.id} align="center">
                  {run.total_distance_km ? `${run.total_distance_km.toFixed(1)} km` : '-'}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Total Time</TableCell>
              {completedRuns.map(run => (
                <TableCell key={run.id} align="center">
                  {run.total_time_hours ? formatTime(run.total_time_hours) : '-'}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Optimization Score</TableCell>
              {completedRuns.map(run => (
                <TableCell key={run.id} align="center">
                  <Chip
                    label={run.optimization_score?.toFixed(1) || '-'}
                    color={run.optimization_score && run.optimization_score > 80 ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Optimization Results
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab label="Runs" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Daily Schedules" icon={<CalendarIcon />} iconPosition="start" />
          <Tab label="Compare" icon={<CompareIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {_error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {_error}
        </Alert>
      )}

      {activeTab === 0 && renderRunsList()}
      {activeTab === 1 && renderDailySchedule()}
      {activeTab === 2 && renderComparison()}
    </Box>
  );
};